import React, { useState } from 'react';
import './OrdersTable.css';
import Pagination from './Pagination';
import TransactionDetailsPanel from './TransactionDetailsPanel';
import useUrlDetails from '../hooks/useUrlDetails';

const TransactionsTable = ({ data, pagination, onPageChange, showTraderName = false }) => {
  const [copiedId, setCopiedId] = useState('');

  // Extract data - support both the new paginated format and the old format
  let transactions = [];
  
  if (data && data.data && Array.isArray(data.data)) {
    // New paginated format
    transactions = data.data;
  } else if (Array.isArray(data)) {
    // Old format - just an array
    transactions = data;
  } else if (data && data.ops && data.ops.length > 0) {
    // Backward compatibility with the old structure
    const firstOp = data.ops[0];
    if (firstOp.list && Array.isArray(firstOp.list)) {
      transactions = firstOp.list;
    } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
      transactions = firstOp.data.list;
    }
  }

  const [selectedTransaction, setSelectedTransaction] = useUrlDetails(transactions, 'transactionId');

  // Sort by creation date (create_time) - newest first
  transactions.sort((a, b) => {
    const timeA = a.create_time || 0;
    const timeB = b.create_time || 0;
    return timeB - timeA;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp * 1000); // If timestamp is in seconds
      if (isNaN(date.getTime())) {
        const date2 = new Date(timestamp); // Try as milliseconds
        if (isNaN(date2.getTime())) return String(timestamp);
        return formatCustomDate(date2);
      }
      return formatCustomDate(date);
    } catch {
      return String(timestamp);
    }
  };

  const formatCustomDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: '1rem', fontWeight: 600 }}>{day}.{month}.{year}</span>
        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{hours}:{minutes}:{seconds}</span>
      </div>
    );
  };

  const getFirstValue = (transaction, keys) => {
    for (const key of keys) {
      const value = transaction[key];
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }
    return null;
  };

  const shortenValue = (value, start = 8, end = 6) => {
    const text = String(value || '');
    if (text.length <= start + end + 3) return text || '-';
    return `${text.slice(0, start)}…${text.slice(-end)}`;
  };

  const formatType = (transaction) => {
    const rawType = getFirstValue(transaction, ['type', 'Type', 'action', 'Action', 'source', 'Source']);
    if (!rawType) return 'Deposit';

    const normalized = String(rawType).toLowerCase();
    if (normalized.includes('deposit') || normalized.includes('balanceadd')) return 'Deposit';
    if (normalized.includes('withdraw') || normalized.includes('balancesubtract')) return 'Withdrawal';
    return String(rawType)
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const formatAmount = (transaction) => {
    const amount = getFirstValue(transaction, ['amount', 'Amount', 'amountUsdt', 'AmountUsdt']);
    const token = getFirstValue(transaction, ['token', 'Token', 'currency', 'Currency']) || 'USDT';
    return amount === null ? '-' : `${amount} ${token}`;
  };

  const formatSource = (transaction) => {
    const source = getFirstValue(transaction, ['source', 'Source']);
    const network = getFirstValue(transaction, ['network', 'Network']);
    const transactionId = getFirstValue(transaction, ['txId', 'TxId', 'transactionId', 'TransactionId']);

    if (network || transactionId || String(source || '').toLowerCase().includes('tron')) {
      return network || 'TRON';
    }
    return 'Manual';
  };

  const copyTransactionId = async (event, transactionId) => {
    event.stopPropagation();
    if (!transactionId) return;
    try {
      await navigator.clipboard.writeText(String(transactionId));
      setCopiedId(String(transactionId));
      window.setTimeout(() => setCopiedId(''), 1800);
    } catch {
      setCopiedId('');
    }
  };

  const displayColumns = [
    {
      key: 'created',
      label: 'Created',
      getValue: (transaction) => formatDate(
        getFirstValue(transaction, ['create_time', 'Created', 'createdAt', 'blockTimestamp'])
      )
    },
    ...(showTraderName ? [{
      key: 'trader',
      label: 'Trader',
      getValue: (transaction) => {
        const name = getFirstValue(transaction, ['traderName', 'TraderName']);
        const login = getFirstValue(transaction, ['traderLogin', 'TraderLogin']);
        const traderId = getFirstValue(transaction, ['traderId', 'TraderId']);
        return (
          <div className="transaction-trader-cell">
            <span>{name || login || traderId || '-'}</span>
            {(login || traderId) && (
              <span>
                {[login && login !== name ? `@${login}` : null, traderId].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
        );
      }
    }] : []),
    {
      key: 'type',
      label: 'Type',
      getValue: formatType
    },
    {
      key: 'amount',
      label: 'Amount',
      getValue: formatAmount
    },
    ...(showTraderName ? [{
      key: 'source',
      label: 'Source',
      getValue: formatSource
    }, {
      key: 'reference',
      label: 'Reference',
      getValue: (transaction) => {
        const transactionId = getFirstValue(
          transaction,
          ['txId', 'TxId', 'transactionId', 'TransactionId', 'hash', 'Hash']
        );
        return (
          <div className="transaction-id-cell" title={transactionId || ''}>
            <span className="transaction-mono">{shortenValue(transactionId, 10, 8)}</span>
            {transactionId && (
              <button
                className="copy-button"
                type="button"
                onClick={(event) => copyTransactionId(event, transactionId)}
              >
                {copiedId === String(transactionId) ? 'Copied' : 'Copy'}
              </button>
            )}
          </div>
        );
      }
    }] : [{
      key: 'from',
      label: 'From',
      getValue: (transaction) => {
        const address = getFirstValue(transaction, ['from', 'From', 'fromAddress', 'FromAddress']);
        return <span className="transaction-mono" title={address || ''}>{shortenValue(address)}</span>;
      }
    },
    {
      key: 'transaction-id',
      label: 'Transaction ID',
      getValue: (transaction) => {
        const transactionId = getFirstValue(
          transaction,
          ['txId', 'TxId', 'transactionId', 'TransactionId', 'hash', 'Hash', 'ref']
        );
        return (
          <div className="transaction-id-cell" title={transactionId || ''}>
            <span className="transaction-mono">{shortenValue(transactionId, 10, 8)}</span>
            {transactionId && (
              <button
                className="copy-button"
                type="button"
                onClick={(event) => copyTransactionId(event, transactionId)}
              >
                {copiedId === String(transactionId) ? 'Copied' : 'Copy'}
              </button>
            )}
          </div>
        );
      }
    }])
  ];

  if (transactions.length === 0) {
    return (
      <div className="orders-empty">
        <p>No data to display</p>
      </div>
    );
  }

  return (
    <>
      <div className="orders-table-container">
        <div className="table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                {displayColumns.map((column) => (
                  <th key={column.key} className={`header-${column.key.toLowerCase()}`}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction, index) => (
                  <tr 
                    key={transaction.id || transaction.ref || index}
                    className="order-row"
                    onClick={() => setSelectedTransaction(transaction)}
                  >
                    {displayColumns.map((column) => {
                      return (
                        <td 
                          key={column.key} 
                          className={`column-${column.key}`}
                          data-label={column.label}
                        >
                          {column.getValue(transaction)}
                        </td>
                      );
                    })}
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && onPageChange && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            itemsPerPage={pagination.limit}
            totalItems={pagination.total}
          />
        )}
      </div>
      <TransactionDetailsPanel 
        transaction={selectedTransaction} 
        onClose={() => setSelectedTransaction(null)}
      />
    </>
  );
};

export default TransactionsTable;
